import {
  type FacebookAuthenticationInput,
  type FacebookBasicCredentials,
  type FacebookCookieCredentials,
} from '@unipile/provider-facebook';
import { type ProviderAuthenticationIntentResponse } from '@unipile/types';
import {
  ScopesConsentScreen,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  UniLoginForm,
  useHostedAuthSession,
  useScopesConsent,
  useStartProviderAuthIntent,
} from '@unipile/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CookiesForm } from './CookiesForm';
import { CredentialsForm } from './CredentialsForm';

type Step = 'login' | 'consent';

export function LoginScreen({
  onSuccess,
  onError,
  onCancel,
}: {
  onSuccess: (data: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { data: session } = useHostedAuthSession();
  // As for LinkedIn, the scopes are shown on the product selection screen after the login, unless there is none.
  const shouldCollectScopesBeforeAuthentication = session?.config?.facebook?.allow_product_selection === false;
  const { scopes, hasScopes, brandName, cancelConsent } = useScopesConsent({ provider: 'facebook' });
  const allowMethods = session?.config?.facebook?.allow_methods || ['credentials', 'cookies', 'unilogin'];
  const [step, setStep] = useState<Step>('login');
  const [credentials, setCredentials] = useState<FacebookBasicCredentials | FacebookCookieCredentials>();

  const { isPending, mutate } = useStartProviderAuthIntent({
    mutationConfig: {
      onSuccess,
      onError: (error) => {
        if (
          error instanceof Error &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'type' in error.response.data &&
          error.response.data.type === 'provider/invalid_credentials'
        ) {
          setStep('login');
        }
        onError(error);
      },
    },
  });

  const buildAuthPayload = (
    nextCredentials: FacebookBasicCredentials | FacebookCookieCredentials,
  ): FacebookAuthenticationInput => ({
    provider: 'facebook',
    credentials: nextCredentials,
    ...(session?.config?.facebook ? { config: session.config.facebook } : {}),
    ...(typeof navigator !== 'undefined' && navigator.userAgent ? { user_agent: navigator.userAgent } : {}),
  });

  const submitCredentials = (nextCredentials: FacebookBasicCredentials | FacebookCookieCredentials) => {
    if (shouldCollectScopesBeforeAuthentication && hasScopes) {
      setCredentials(nextCredentials);
      setStep('consent');
      return;
    }

    mutate({ data: buildAuthPayload(nextCredentials) });
  };

  if (step === 'consent' && credentials) {
    return (
      <ScopesConsentScreen
        scopes={scopes}
        brandName={brandName}
        onAccept={() => mutate({ data: buildAuthPayload(credentials) })}
        onCancel={() => {
          cancelConsent();
          setStep('login');
        }}
        isPending={isPending}
      />
    );
  }

  return (
    <>
      <Tabs
        defaultValue={
          allowMethods.includes('credentials') ? 'credentials' : allowMethods.includes('cookies') ? 'cookies' : 'unilogin'
        }
      >
        {allowMethods.length > 1 && (
          <TabsList className="w-full">
            {allowMethods.includes('credentials') && (
              <TabsTrigger className="w-full" value="credentials">
                {t('LoginScreen.method.credentials', { ns: 'facebook' })}
              </TabsTrigger>
            )}
            {allowMethods.includes('cookies') && (
              <TabsTrigger className="w-full" value="cookies">
                {t('LoginScreen.method.cookies', { ns: 'facebook' })}
              </TabsTrigger>
            )}
            {allowMethods.includes('unilogin') && (
              <TabsTrigger className="w-full" value="unilogin">
                {session?.unilogin_tab_name || 'UniLogin'}
              </TabsTrigger>
            )}
          </TabsList>
        )}

        {allowMethods.includes('unilogin') && (
          <TabsContent value="unilogin">
            <UniLoginForm provider="facebook" onSuccess={onSuccess} onError={onError} onCancel={onCancel} />
          </TabsContent>
        )}

        <TabsContent value="credentials">
          <CredentialsForm onSubmit={submitCredentials} isPending={isPending && !hasScopes} />
        </TabsContent>

        <TabsContent value="cookies">
          <CookiesForm onSubmit={submitCredentials} isPending={isPending && !hasScopes} />
        </TabsContent>
      </Tabs>
    </>
  );
}
